import { useEffect, useState, FC } from 'react';
import { Unauthorized } from '@algoan/deep-dive/unauthorized';
import { useTggl } from 'react-tggl-client';
import { useDispatch, useSelector } from '@algoan/dashboard/store/store';
import { logout, redirectToLogin, setAuthToken } from '@algoan/dashboard/store/thunks';
import { useAccessControl } from '../hooks/use-access-control';
import { useIsFirstConnectionAfterDashboardRegistration } from '../hooks/use-is-first-connection-after-dashboard-registration';
import { AccessTokenPayload, DashboardRoles } from '@algoan/dashboard/model';
import { useRouter } from 'next/router';
import {
  userRoleSelector,
  isUserConnectedSelector,
  selectUserInformation,
  skipOnboardingSelector,
} from '@algoan/dashboard/store/selectors';
import { parseJwtPayload, useSession } from '@algoan/authentication';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { openOnboarding } from '@algoan/dashboard/store/slice';
import { LogAction, monitoring } from '@algoan/dashboard/utils';

const isPathForbiddenForInitiator = (pathname: string): boolean => {
  const pathForbiddenForInitiator = ['organization', 'activity'];

  return pathForbiddenForInitiator.some((path) => pathname.includes(path));
};

export const Protected: FC<{ render: () => JSX.Element }> = ({ render }) => {
  const userHasAnAccessToken = useSelector(isUserConnectedSelector);
  const [canRender, setCanRender] = useState(false);
  const [isSSR, setIsSSR] = useState(true);
  const dispatch = useDispatch();
  const { session, status } = useSession();
  const userInformation = useSelector(selectUserInformation);
  const hasRequiredRoles = useAccessControl([
    DashboardRoles.ADMIN,
    DashboardRoles.CONSULTANT,
    DashboardRoles.INITIATOR,
  ]);
  const { isInitiator } = useSelector(userRoleSelector);
  const router = useRouter();
  const isFirstConnectionAfterDashboardRegistration = useIsFirstConnectionAfterDashboardRegistration();

  const { updateContext } = useTggl();
  const skipOnboarding = useSelector(skipOnboardingSelector);

  const isOnPublicPage = ['/email-verified', '/auth/sign-in', '/auth/error'].includes(router.route);

  useEffect(() => {
    if (isOnPublicPage) {
      setCanRender(true);
      return;
    }

    const userIsLoggedIn = status === 'authenticated';

    if (status === 'unauthenticated') {
      monitoring.logAction(LogAction.LOGIN_REDIRECTION, {
        message: 'User not authenticated',
        status,
        pathname: window.location.pathname,
      });
      // Redirects to log in form.
      dispatch(redirectToLogin());
      return;
    }

    if (userIsLoggedIn && !userHasAnAccessToken && !window.isRedirectingToKeycloak && !canRender) {
      dispatch(setAuthToken(session));
      logAmplitudeEvent(AmplitudeEvent.LOGIN_SUCCESS);
      monitoring.logAction(LogAction.LOGIN_SUCCESS, {
        message: 'User logged in successfully',
        status,
        pathname: window.location.pathname,
        session,
      });
      const accessTokenPayload = parseJwtPayload<AccessTokenPayload>(session.data.accessToken);
      if (!skipOnboarding && !accessTokenPayload?.skipOnboarding) {
        dispatch(openOnboarding());
      }
      return;
    }

    if (userHasAnAccessToken && isFirstConnectionAfterDashboardRegistration) {
      void router.push('/loading');
    }

    if (session?.data.accessToken) {
      const accessTokenPayload = parseJwtPayload<AccessTokenPayload>(session.data.accessToken);
      updateContext({ email: accessTokenPayload.email, organizationId: accessTokenPayload.organizationIds });
    }

    if (isInitiator && isPathForbiddenForInitiator(router.asPath)) {
      void router.push('/');
      return;
    }

    setCanRender(hasRequiredRoles || (userHasAnAccessToken && isFirstConnectionAfterDashboardRegistration));
  }, [
    userHasAnAccessToken,
    hasRequiredRoles,
    canRender,
    router.asPath,
    isInitiator,
    isOnPublicPage,
    status,
    isFirstConnectionAfterDashboardRegistration,
    session,
    router,
    dispatch,
    updateContext,
    skipOnboarding,
  ]);

  // immediately set screen to blank when logging out
  useEffect(() => {
    if (!userHasAnAccessToken && canRender) {
      setCanRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userHasAnAccessToken]);

  useEffect(() => {
    setIsSSR(false);
  }, []);

  if (isSSR) {
    return null;
  }

  if (canRender) {
    return render();
  }

  const rolesHaveBeenReturnedFromKeycloakWithNoAccess = userInformation?.roles !== undefined && !hasRequiredRoles;

  // condition will resolve to an empty div when the page is refreshing and the user does have access
  return rolesHaveBeenReturnedFromKeycloakWithNoAccess && !isFirstConnectionAfterDashboardRegistration ? (
    <Unauthorized applicationName="Dashboard" onButtonClick={() => dispatch(logout())} />
  ) : (
    <div />
  );
};
